import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import {
  getAppMarketplaceMatches,
  getAppMarketplaceMatchRequests,
  getAppMarketplacePitches,
  getAppMarketplaceSummary,
  getAppPlayerFootballJourney,
  searchAppMarketplacePlayers,
} from '../services/api';

const fmtDate = d => d ? new Date(d).toLocaleString('en-GB', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';
const money = (value, currency = 'USD') => `${currency} ${Number(value || 0).toFixed(2)}`;
const asArray = value => Array.isArray(value) ? value : [];

const statusClass = status => ({
  pending: 'badge badge-blue',
  accepted: 'badge badge-green',
  declined: 'badge badge-red',
  cancelled: 'badge badge-gray',
  published: 'badge badge-green',
  full: 'badge badge-blue',
  completed: 'badge badge-gray',
  draft: 'badge badge-gray',
  in_progress: 'badge badge-blue',
})[status] || 'badge badge-gray';

const Stat = ({ label, value, sub }) => (
  <div className="stat-card">
    <div className="stat-label">{label}</div>
    <div className="stat-value">{value ?? 0}</div>
    {sub && <div className="stat-sub">{sub}</div>}
  </div>
);

const Empty = ({ label }) => (
  <div style={{ padding:24, color:'var(--text-dim)', fontSize:13, textAlign:'center', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10 }}>
    {label}
  </div>
);

function RequestsTable({ rows }) {
  const safeRows = asArray(rows);
  if (!safeRows.length) return <Empty label="No player match requests found" />;
  return (
    <div style={{ overflowX:'auto' }}>
      <table className="table">
        <thead><tr><th>Request</th><th>Player</th><th>Pitch</th><th>Organizer</th><th>Kickoff</th><th>Status</th></tr></thead>
        <tbody>
          {safeRows.map(row => (
            <tr key={row.id}>
              <td>
                <div style={{ color:'var(--text-primary)', fontWeight:600 }}>{row.title || 'Match request'}</div>
                <div style={{ color:'var(--text-dim)', fontSize:12 }}>{row.format} · {row.skillLevel} · {row.totalPlayers} players</div>
              </td>
              <td>{row.playerName || '—'}</td>
              <td>{row.pitchName || '—'}</td>
              <td>{row.organizerName || '—'}</td>
              <td>{fmtDate(row.kickoffAt)}</td>
              <td><span className={statusClass(row.status)}>{row.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MatchesTable({ rows }) {
  const safeRows = asArray(rows);
  if (!safeRows.length) return <Empty label="No marketplace matches found" />;
  return (
    <div style={{ overflowX:'auto' }}>
      <table className="table">
        <thead><tr><th>Match</th><th>Pitch</th><th>Kickoff</th><th>Slots</th><th>Price</th><th>Status</th></tr></thead>
        <tbody>
          {safeRows.map(row => (
            <tr key={row.id}>
              <td>
                <div style={{ color:'var(--text-primary)', fontWeight:600 }}>{row.title || row.location}</div>
                <div style={{ color:'var(--text-dim)', fontSize:12 }}>{row.format} · {row.skillLevel}</div>
              </td>
              <td>{row.pitch?.name || row.location || '—'}</td>
              <td>{fmtDate(row.kickoffAt)}</td>
              <td>{row.confirmedBookings || 0}/{row.totalSlots || 0}</td>
              <td>{money(row.pricePerPlayer, row.currency)}</td>
              <td><span className={statusClass(row.status)}>{row.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PitchesGrid({ rows }) {
  const safeRows = asArray(rows);
  if (!safeRows.length) return <Empty label="No pitches found" />;
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:12 }}>
      {safeRows.map(row => (
        <div key={row.id} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:14 }}>
          <div style={{ color:'var(--text-primary)', fontWeight:700 }}>{row.name}</div>
          <div style={{ color:'var(--text-dim)', fontSize:12, marginTop:4 }}>{row.location || row.city || 'No location'}</div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12, gap:10 }}>
            <span style={{ color:'#A7F432', fontSize:13 }}>{money(row.price, row.currency)}</span>
            <span className={row.isActive === false ? 'badge badge-gray' : 'badge badge-green'}>{row.isActive === false ? 'Inactive' : 'Active'}</span>
          </div>
          <div style={{ color:'var(--text-dim)', fontSize:12, marginTop:8 }}>{row.organizerName || 'No organizer'}</div>
        </div>
      ))}
    </div>
  );
}

function JourneyPanel({ token, toast }) {
  const [playerSearch, setPlayerSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [journey, setJourney] = useState(null);
  const [error, setError] = useState('');
  const [searching, setSearching] = useState(false);
  const [loadingJourney, setLoadingJourney] = useState(false);

  const searchPlayers = async e => {
    e?.preventDefault();
    const term = playerSearch.trim();
    if (!term) {
      setSubmittedSearch('');
      setPlayers([]);
      setJourney(null);
      setSelectedPlayer(null);
      setError('');
      return;
    }
    setSearching(true);
    setSubmittedSearch(term);
    setJourney(null);
    setSelectedPlayer(null);
    setError('');
    try {
      const res = await searchAppMarketplacePlayers(token, { page:1, limit:20, search:term });
      setPlayers(asArray(res.data));
    } catch (err) {
      setPlayers([]);
      setError(err.message || 'Unable to search players');
      toast?.(err.message, 'error');
    } finally {
      setSearching(false);
    }
  };

  const openJourney = async player => {
    setSelectedPlayer(player);
    setLoadingJourney(true);
    setError('');
    try {
      const res = await getAppPlayerFootballJourney(token, player.id);
      setJourney(res.data || null);
    } catch (err) {
      setJourney(null);
      setError(err.message || 'Unable to load football journey');
      toast?.(err.message, 'error');
    } finally {
      setLoadingJourney(false);
    }
  };

  const stats = journey?.stats || {};
  const player = journey?.player || {};
  const playerRows = asArray(players);

  return (
    <div>
      <form onSubmit={searchPlayers} className="toolbar">
        <input className="search-input" value={playerSearch} onChange={e => setPlayerSearch(e.target.value)} placeholder="Search player by name or email…" />
        <button className="btn btn-ghost" disabled={searching}>{searching ? 'Searching…' : 'Search'}</button>
      </form>

      {!journey && (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {error && <Empty label={error} />}
          {playerRows.length === 0 && !error ? (
            <Empty label={submittedSearch ? `No result found for "${submittedSearch}"` : 'Search for a player to view their mobile football journey'} />
          ) : playerRows.map(row => (
            <button
              key={row.id}
              onClick={() => openJourney(row)}
              style={{ textAlign:'left', cursor:'pointer', background:selectedPlayer?.id === row.id ? 'rgba(167,244,50,0.08)' : 'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:14, display:'flex', justifyContent:'space-between', gap:16, alignItems:'center' }}
            >
              <div>
                <div style={{ color:'var(--text-primary)', fontWeight:700 }}>{row.name}</div>
                <div style={{ color:'var(--text-dim)', fontSize:12, marginTop:4 }}>{row.position || 'No position'} · {row.email || 'No email'}</div>
              </div>
              <div style={{ display:'flex', gap:10, color:'var(--text-dim)', fontSize:12, flexWrap:'wrap', justifyContent:'flex-end' }}>
                <span>{row.stats?.bookings || 0} bookings</span>
                <span>{row.stats?.matchRequests || 0} requests</span>
                <span>{row.stats?.highlights || 0} highlights</span>
              </div>
            </button>
          ))}
          {loadingJourney && <div style={{ color:'var(--text-dim)', fontSize:13, padding:12 }}>Loading football journey…</div>}
        </div>
      )}

      {journey && (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <button className="btn btn-ghost" onClick={() => setJourney(null)} style={{ alignSelf:'flex-start' }}>Back to results</button>
          <div style={{ display:'flex', justifyContent:'space-between', gap:16, flexWrap:'wrap', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:16 }}>
            <div>
              <div style={{ color:'var(--text-primary)', fontWeight:700 }}>{[player.firstName, player.lastName].filter(Boolean).join(' ') || player.email}</div>
              <div style={{ color:'var(--text-dim)', fontSize:12, marginTop:4 }}>{player.position || 'No position'} · {player.email || 'No email'}</div>
            </div>
            <span className="badge badge-green">{journey.progress?.tier || 'development'}</span>
          </div>
          <div className="stats-grid">
            <Stat label="Matches" value={stats.matches} sub={`${stats.completedMatches || 0} completed`} />
            <Stat label="Upcoming" value={stats.upcomingMatches} sub="Confirmed bookings" />
            <Stat label="Highlights" value={stats.highlights} sub="Uploads" />
            <Stat label="Requests" value={stats.matchRequests} sub="Pitch requests" />
          </div>
          <RequestsTable rows={journey.matchRequests || []} />
        </div>
      )}
    </div>
  );
}

export default function MobileMarketplace({ toast }) {
  const { token } = useApp();
  const [tab, setTab] = useState('requests');
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [matches, setMatches] = useState([]);
  const [pitches, setPitches] = useState([]);
  const [pitchTotal, setPitchTotal] = useState(0);
  const [pitchPage, setPitchPage] = useState(1);
  const [pitchSearch, setPitchSearch] = useState('');
  const [pitchCity, setPitchCity] = useState('');
  const [loading, setLoading] = useState(true);
  const [pitchLoading, setPitchLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const pitchLimit = 50;

  const loadPitches = useCallback(async (p = 1, q = '', city = '') => {
    setPitchLoading(true);
    try {
      const res = await getAppMarketplacePitches(token, { page:p, limit:pitchLimit, search:q, city });
      setPitches(asArray(res.data?.pitches || res.data));
      setPitchTotal(res.pagination?.total || res.data?.total || res.total || 0);
      setPitchPage(p);
    } catch(e) {} finally { setPitchLoading(false); }
  }, [token]);

  const load = useCallback(async (q = '') => {
    setLoading(true);
    setError('');
    try {
      const params = { page:1, limit:30, search:q };
      const [s, r, m] = await Promise.all([
        getAppMarketplaceSummary(token),
        getAppMarketplaceMatchRequests(token, params),
        getAppMarketplaceMatches(token, params),
      ]);
      setSummary(s.data || null);
      setRequests(asArray(r.data));
      setMatches(asArray(m.data));
    } catch (err) {
      setRequests([]);
      setMatches([]);
      setError(err.message || 'Unable to load mobile marketplace');
      toast?.(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => { load(''); loadPitches(1); }, [load, loadPitches]);

  const totals = useMemo(() => ({
    pendingRequests: summary?.matchRequests?.pending || 0,
    activePitches: summary?.pitches?.active || 0,
    publishedMatches: summary?.matches?.published || 0,
    confirmedBookings: summary?.bookings?.confirmed || 0,
  }), [summary]);

  const submitSearch = e => {
    e.preventDefault();
    load(search);
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, gap:12, flexWrap:'wrap' }}>
        <div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:22, color:'var(--text-primary)', margin:0 }}>Mobile Marketplace</h1>
          <p style={{ color:'var(--text-dim)', fontSize:13, margin:'4px 0 0' }}>Pitch booking flow, player match requests, published matches, and football journey data</p>
        </div>
        <form onSubmit={submitSearch} style={{ display:'flex', gap:8 }}>
          <input className="search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title, pitch, location" />
          <button className="btn btn-ghost">Search</button>
        </form>
      </div>

      <div className="stats-grid" style={{ marginBottom:20 }}>
        <Stat label="Pending requests" value={totals.pendingRequests} sub="Need organizer action" />
        <Stat label="Active pitches" value={totals.activePitches} sub="Visible on mobile" />
        <Stat label="Published matches" value={totals.publishedMatches} sub="Joinable games" />
        <Stat label="Confirmed bookings" value={totals.confirmedBookings} sub="Players joined" />
      </div>

      <div style={{ display:'flex', gap:4, marginBottom:16, borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
        {[
          ['requests', 'Match Requests'],
          ['matches', 'Matches'],
          ['pitches', 'Pitches'],
          ['journey', 'Football Journey'],
        ].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ background:'none', border:'none', cursor:'pointer', padding:'10px 14px', color:tab === id ? '#4B9EF5' : 'var(--text-dim)', borderBottom:tab === id ? '2px solid #4B9EF5' : '2px solid transparent', fontWeight:600 }}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)' }}>Loading mobile marketplace…</div>
      ) : error ? (
        <Empty label={error} />
      ) : (
        <>
          {tab === 'requests' && <RequestsTable rows={requests} />}
          {tab === 'matches' && <MatchesTable rows={matches} />}
          {tab === 'pitches' && (
            <div>
              <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
                <input
                  className="search-input"
                  style={{ flex:1, minWidth:200, maxWidth:340 }}
                  placeholder="Search pitches by name, city, location..."
                  value={pitchSearch}
                  onChange={e => setPitchSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && loadPitches(1, pitchSearch, pitchCity)}
                />
                <input
                  className="search-input"
                  style={{ width:160 }}
                  placeholder="Filter by city..."
                  value={pitchCity}
                  onChange={e => setPitchCity(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && loadPitches(1, pitchSearch, pitchCity)}
                />
                <button className="btn btn-ghost" onClick={() => loadPitches(1, pitchSearch, pitchCity)}>
                  Search
                </button>
                {(pitchSearch || pitchCity) && (
                  <button className="btn btn-ghost" onClick={() => { setPitchSearch(''); setPitchCity(''); loadPitches(1, '', ''); }}>
                    Clear
                  </button>
                )}
              </div>
              {pitchLoading ? (
                <div style={{ textAlign:'center', padding:40, color:'var(--text-dim)' }}>Loading pitches...</div>
              ) : (
                <PitchesGrid rows={pitches} />
              )}
              {pitchTotal > pitchLimit && (
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:16 }}>
                  <span style={{ color:'var(--text-dim)', fontSize:13 }}>
                    {(pitchPage-1)*pitchLimit+1}–{Math.min(pitchPage*pitchLimit, pitchTotal)} of {pitchTotal} pitches
                  </span>
                  <div style={{ display:'flex', gap:8 }}>
                    <button className="btn btn-ghost" disabled={pitchPage<=1}
                      onClick={() => loadPitches(pitchPage-1, pitchSearch, pitchCity)}>← Prev</button>
                    <button className="btn btn-ghost" disabled={pitchPage*pitchLimit>=pitchTotal}
                      onClick={() => loadPitches(pitchPage+1, pitchSearch, pitchCity)}>Next →</button>
                  </div>
                </div>
              )}
            </div>
          )}
          {tab === 'journey' && <JourneyPanel token={token} toast={toast} />}
        </>
      )}
    </div>
  );
}
